if (!customElements.get('product-media-counter')) {
  class ProductMediaCounter extends HTMLElement {
    connectedCallback() {
      this.preserveInitialPagePosition();
      this.gallery = this.closest('media-gallery');
      this.slider = this.gallery?.querySelector('slider-element');
      if (!this.slider) return;
      this.variantSource = document.forms[this.gallery.getAttribute('form')] || document;

      this.handleSlideChange = this.handleSlideChange.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);

      this.slider.addEventListener('slider:change', this.handleSlideChange);
      this.slider.addEventListener('scroll', this.handleScroll, { passive: true });
      this.variantSource.addEventListener('variant:change', this.handleVariantChange);
      requestAnimationFrame(() => this.update());
    }

    preserveInitialPagePosition() {
      if (window.innerWidth > 767 || window.location.hash || window.__thinkcarProductTopGuard) return;

      const navigationType = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;
      if (navigationType === 'back_forward') return;

      window.__thinkcarProductTopGuard = true;
      history.scrollRestoration = 'manual';

      let userInteracted = false;
      let correcting = false;
      const interactionEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
      const stopGuard = () => {
        userInteracted = true;
        cleanup();
      };
      const keepAtTop = () => {
        if (userInteracted || correcting || window.scrollY < 2) return;
        correcting = true;
        window.scrollTo(0, 0);
        requestAnimationFrame(() => { correcting = false; });
      };
      const cleanup = () => {
        window.removeEventListener('scroll', keepAtTop);
        window.removeEventListener('pageshow', keepAtTop);
        window.removeEventListener('load', keepAtTop);
        interactionEvents.forEach((eventName) => window.removeEventListener(eventName, stopGuard));
        clearTimeout(window.__thinkcarProductTopGuardTimer);
        delete window.__thinkcarProductTopGuard;
      };

      window.addEventListener('scroll', keepAtTop, { passive: true });
      window.addEventListener('pageshow', keepAtTop, { passive: true });
      window.addEventListener('load', keepAtTop, { passive: true });
      interactionEvents.forEach((eventName) => window.addEventListener(eventName, stopGuard, { passive: true, once: true }));

      keepAtTop();
      window.__thinkcarProductTopGuardTimer = setTimeout(cleanup, 2200);
    }

    disconnectedCallback() {
      this.slider?.removeEventListener('slider:change', this.handleSlideChange);
      this.slider?.removeEventListener('scroll', this.handleScroll);
      this.variantSource?.removeEventListener('variant:change', this.handleVariantChange);
      clearTimeout(this.variantUpdateTimer);
      cancelAnimationFrame(this.scrollFrame);
    }

    get visibleMedia() {
      return Array.from(this.slider?.querySelectorAll('.product__media[data-media-id]') || []).filter((media) => {
        return !media.hidden && media.clientWidth > 0 && getComputedStyle(media).display !== 'none';
      });
    }

    handleSlideChange(event) {
      this.update(event.detail?.currentElement);
    }

    handleScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const media = this.visibleMedia;
        if (!media.length) return;

        const firstOffset = media[0].offsetLeft;
        const scrollPosition = Math.abs(this.slider.scrollLeft);
        const activeMedia = media.reduce((closest, item) => {
          const itemPosition = Math.abs(item.offsetLeft - firstOffset);
          const closestPosition = Math.abs(closest.offsetLeft - firstOffset);
          return Math.abs(itemPosition - scrollPosition) < Math.abs(closestPosition - scrollPosition)
            ? item
            : closest;
        }, media[0]);

        this.update(activeMedia);
      });
    }

    handleVariantChange() {
      clearTimeout(this.variantUpdateTimer);
      this.variantUpdateTimer = setTimeout(() => this.update(), 100);
    }

    update(activeMedia) {
      const media = this.visibleMedia;
      if (!media.length) return;

      const currentMedia = activeMedia && media.includes(activeMedia)
        ? activeMedia
        : media[(this.slider.currentPage || 1) - 1] || media[0];
      const current = Math.max(1, media.indexOf(currentMedia) + 1);
      const total = media.length;

      this.querySelector('[data-current]').textContent = current;
      this.querySelector('[data-total]').textContent = total;
      this.setAttribute('aria-label', `Product media ${current} of ${total}`);
      this.hidden = total < 2;
    }
  }

  customElements.define('product-media-counter', ProductMediaCounter);
}